/**
 * The canvas's right-click menu: the same icon-only bar the project viewer
 * shows next to the cursor for a selected object (potree ContextMenu.ts),
 * here for the selected shapes or group. Every item acts and closes it;
 * Escape, a press elsewhere or the wheel close it too.
 */

import classNames from 'classnames';
import { memo, useEffect, useRef } from 'react';
import type { HeroIcon } from '../../../types';

export interface CanvasContextMenuItem {
  id: string;
  icon: HeroIcon;
  /** Tooltip (with its shortcut, when it has one). */
  label: string;
  onClick: () => void;
  danger?: boolean;
}

/** Distance from the cursor to the menu's corner (px), as in the viewer. */
const OFFSET_PX = 12;

const CanvasContextMenu_: React.FC<{ x: number; y: number; bounds: { width: number; height: number }; items: readonly CanvasContextMenuItem[]; onClose: () => void }> = ({ x, y, bounds, items, onClose }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      if (ref.current && e.target instanceof Node && ref.current.contains(e.target)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    // Capture: closes even when the canvas handles the press itself.
    window.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('wheel', onClose, { passive: true });
    return () => {
      window.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('wheel', onClose);
    };
  }, [onClose]);

  // Kept inside the canvas: 32 px buttons, 4 px gaps and padding.
  const width = items.length * 36 + 4;
  const left = Math.max(0, Math.min(x + OFFSET_PX, bounds.width - width));
  const top = Math.max(0, Math.min(y + OFFSET_PX, bounds.height - 40));

  return (
    <div ref={ref} role="menu" style={{ left, top }} onContextMenu={(e) => e.preventDefault()} className="absolute z-20 flex gap-1 rounded-md border border-white/15 bg-[rgba(20,20,20,0.92)] p-1 shadow-[0_4px_12px_rgba(0,0,0,0.4)]">
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          role="menuitem"
          title={item.label}
          aria-label={item.label}
          onClick={() => {
            onClose();
            item.onClick();
          }}
          className={classNames('flex h-8 w-8 items-center justify-center rounded text-white/85 transition-colors hover:bg-white/10', item.danger && 'hover:text-error-red-500')}
        >
          <item.icon className="h-[18px] w-[18px]" />
        </button>
      ))}
    </div>
  );
};

export const CanvasContextMenu = memo(CanvasContextMenu_);
